import { profilesView, clampLayout, startPayload, SERVER_ACCEPTS } from '../lib/newbuild-model.js';
import { clientHues, byKey } from '../lib/variant-hues.js';

const SABRE_STAGE_NAMES = ['Intake', 'Design analysis', 'Sabre decision', 'Deterministic build', 'Deterministic QA', 'Developer approval'];
const SABRE_PROFILE_RECIPES = [
  'control.metadata', 'control.omit', 'control.esp', 'image.edge_to_edge',
  'image.inset', 'live.center.body', 'live.center.headline', 'live.center.label',
  'live.center.heading', 'live.left.body', 'live.right.job', 'live.left.legal',
  'hybrid.icon_copy',
];

// Small "coming soon" marker — reused for V1-gated options (specialized variants,
// ESP targeting, non-Balanced model tiers) so the UI doesn't overpromise features
// the single backend swarm can't deliver yet.
function SoonChip() {
  return <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--bg-2)', color: 'var(--muted)', padding: '2px 6px', borderRadius: 5, flexShrink: 0 }}>Soon</span>;
}

// Parse a static cost range like "$0.18–0.32" to its midpoint, for budget math
// when a tier has no live average yet.
function parseCostMid(s) {
  const nums = (String(s).match(/[\d.]+/g) || []).map(Number).filter(Number.isFinite);
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0.3;
}

function NewBuild({ go, openJob }) {
  const { clients } = window.MAV;
  const myTeam = window.MAV.teamForMember(window.MAV.currentUser);
  const myPath = window.MAV.teamPath(myTeam && myTeam.id);
  // ORG-02: for sme/dev the "assigned" scope is REAL now — /me carries the caller's
  // active client_assignments (server-authoritative), replacing the pods.clientSlugs
  // mock. Managers (PM/admin/TL) keep the full list ("this becomes locked to your
  // team in a later release" — this is that release, for the client-scoped seats).
  const _viewer = (window.RBAC && window.RBAC.viewer && window.RBAC.viewer()) || null;
  const _role = (window.RBAC && window.RBAC.roleForContext && window.RBAC.roleForContext(_viewer)) || null;
  const isClientScoped = _role === 'sme' || _role === 'dev';
  const myAssigned = ((window.MAV.me && window.MAV.me.assignments) || []).map((a) => a.clientSlug);
  // Only the explicit mock team lane has an org-wide list. Live clients are workspace-scoped.
  const mockTeamScope = !window.MAV.__live && typeof window.MAV.currentUser === 'string' && !!myTeam;
  const [scope, setScope] = React.useState(isClientScoped || mockTeamScope ? 'team' : 'all');   // team | all | standard
  const standard = scope === 'standard';
  const [stdLayout, setStdLayout] = React.useState(() => clampLayout());
  const scopeOptions = [
    ...(isClientScoped
      ? [{ k: 'team', ico: I.users, l: 'My clients', s: 'assigned to you', n: myAssigned.length, count: myAssigned.length + ' assigned' }]
      : mockTeamScope
        ? [{ k: 'team', ico: I.users, l: 'My team', s: myTeam.name, n: myTeam.clientSlugs.length },
          { k: 'all', ico: I.layers, l: 'All clients', s: 'every team', n: clients.length }]
        : [{ k: 'all', ico: I.users, l: 'Workspace clients', s: 'this workspace', n: clients.length }]),
    { k: 'standard', ico: I.doc, l: 'Standard HTML', s: 'no client', n: null },
  ];
  const [client, setClient] = React.useState(
    (isClientScoped && myAssigned[0]) || (myTeam && myTeam.clientSlugs[0]) || 'synchrony');
  const [preset, setPreset] = React.useState('standard');   // client specialization preset
  const [statsClient, setStatsClient] = React.useState(null); // open per-client tracking dashboard
  const [autoDetect, setAutoDetect] = React.useState(true);
  const [source, setSource] = React.useState('pdf');
  const [tier, setTier] = React.useState('balanced');
  const [search, setSearch] = React.useState('');
  const [esp] = React.useState('Salesforce Marketing Cloud (SFMC)');
  const [espSummary, setEspSummary] = React.useState('No ESP (placeholders)');
  const [espDetail, setEspDetail] = React.useState(null);
  const [instr, setInstr] = React.useState('');
  const [extraFiles, setExtraFiles] = React.useState([]);
  const [afterburnerOn, setAfterburnerOn] = React.useState(false);
  // PY-10 Pylon engine selector (PY-02 Decision 1: a FLAT `engine` enum on the wire,
  // NO agent/mode). codex live + default; claude/legacy "soon" (disabled); router
  // ("Bullseye") a selectable preview that reveals the model tier — the multi-model
  // swarm concept the single-binary CLI agents (codex/claude) don't use. Mirrors
  // engines.js; promoting an engine = drop its `soon`. devtest is a registry label, not a wire field.
  const agents = [
    { id: 'sabre', label: 'Sabre', engine: 'sabre', desc: 'Contract-driven · region build', devtest: true, ico: 'jetSolid' },
    { id: 'codex', label: 'Codex CLI', engine: 'codex', desc: 'Agentic CLI · holistic build', devtest: true },
    { id: 'bullseye', label: 'Bullseye', engine: 'router', desc: 'Coming soon', soon: true, preview: true },
    { id: 'swarm', label: 'Swarm Build', engine: 'legacy', desc: 'Coming soon', soon: true },
  ];
  // VD-03: prefill the engine from the active workspace's default (RBAC.activeEngine). Only
  // prefill to a SELECTABLE engine — a workspace set to a not-yet-live engine (bullseye/legacy
  // are `soon`) falls back to codex rather than preselecting a disabled option; when those are
  // promoted (drop `soon`) the prefill honors them. LIVE-WIRE: PY-44 makes the workspace default
  // a real column (rbac.js reads it there instead of localStorage).
  const _engineToAgent = { sabre: 'sabre', codex: 'codex', bullseye: 'bullseye', legacy: 'swarm', router: 'bullseye' };
  const _enginePrefill = (() => {
    const eng = (window.RBAC && window.RBAC.activeEngine) ? window.RBAC.activeEngine() : 'codex';
    const a = agents.find(x => x.id === (_engineToAgent[eng] || 'codex'));
    return (a && !a.soon) ? a.id : 'codex';
  })();
  const [agent, setAgent] = React.useState(_enginePrefill);
  const agentObj = agents.find(a => a.id === agent) || agents[0];
  const showModelTier = agent === 'bullseye';   // only the multi-model swarm/router engine carries a tier
  // Live pre-flight data: per-tier average cost (last 100 builds/tier) + the
  // live OpenRouter balance. Both fetched on mount; null until they land (the
  // UI falls back to static estimates so the demo still renders server-less).
  const [tierCostData, setTierCostData] = React.useState(null);
  const [credits, setCredits] = React.useState(null);
  // build-name + figma-url are now bound to state (were uncontrolled / hardcoded
  // in the prototype). They're the two fields the swarm trigger actually needs.
  const [buildName, setBuildName] = React.useState('demo_build');
  const [figmaUrl, setFigmaUrl] = React.useState('');
  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState(null);
  const [savedStarts, setSavedStarts] = React.useState([]);
  const [checkingStart, setCheckingStart] = React.useState(null);
  const refreshSavedStarts = () => {
    try { setSavedStarts(window.GooseAPI.savedBuildRequests()); }
    catch { setSavedStarts([]); } // context transition clears the previous actor's list
  };
  React.useEffect(() => {
    refreshSavedStarts();
    let key = window.MAV.__contextKey, epoch = window.MAV.__contextEpoch;
    const refresh = () => {
      if (key !== window.MAV.__contextKey || epoch !== window.MAV.__contextEpoch) {
        key = window.MAV.__contextKey; epoch = window.MAV.__contextEpoch;
        setStarting(false); setCheckingStart(null); setStartError(null);
      }
      refreshSavedStarts();
    };
    window.addEventListener('goose-rbac-change', refresh);
    return () => window.removeEventListener('goose-rbac-change', refresh);
  }, []);
  // PDF upload state. uploadId stays null until the user drops a real PDF;
  // when null, the server falls back to the pipeline_demo_1 demo brief.
  const [uploadId, setUploadId] = React.useState(null);
  const [uploadedFiles, setUploadedFiles] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const [sourceFiles, setSourceFiles] = React.useState([]);   // retained source File[] so extras can re-upload alongside it
  const extraInputRef = React.useRef(null);
  // Developer-gold profile onboarding. Active profiles are immutable reviewed
  // chains; the blank selection means the built-in Synchrony Standard profile.
  const [variantProfiles, setVariantProfiles] = React.useState([]);
  const [profilesState, setProfilesState] = React.useState(() => profilesView(null, client));
  const profilesRequest = React.useRef(null);
  const [variantProfileId, setVariantProfileId] = React.useState('');
  const [profileLoadError, setProfileLoadError] = React.useState(null);
  const [profileOnboardingOpen, setProfileOnboardingOpen] = React.useState(false);
  const [profileVariantId, setProfileVariantId] = React.useState('');
  const [profileSubBrandId, setProfileSubBrandId] = React.useState('synchrony');
  const [profileFiles, setProfileFiles] = React.useState([]);
  const [profileProposal, setProfileProposal] = React.useState(null);
  const [profileDraftPolicies, setProfileDraftPolicies] = React.useState([]);
  const [profileResolvedFindings, setProfileResolvedFindings] = React.useState([]);
  const [profileEditSummary, setProfileEditSummary] = React.useState('');
  const [profileApprovalQueue, setProfileApprovalQueue] = React.useState([]);
  const [profileWorking, setProfileWorking] = React.useState(false);
  const [profileError, setProfileError] = React.useState(null);
  const profileInputRef = React.useRef(null);
  const canApproveProfile = !!(window.RBAC && window.RBAC.requireRole && window.RBAC.requireRole('upload_decision'));
  const editablePolicy = (row) => ({
    semantic_role: row.semantic_role,
    disposition: row.disposition,
    owner: row.owner,
    implementation_mode: row.implementation_mode,
    renderer_recipe: row.renderer_recipe,
    responsive_behavior: row.responsive_behavior,
    dark_mode_behavior: row.dark_mode_behavior,
  });
  const adoptProfileDraft = (proposal) => {
    setProfileProposal(proposal);
    setProfileDraftPolicies((proposal && proposal.candidate_profile && proposal.candidate_profile.region_policies || []).map(editablePolicy));
    setProfileResolvedFindings((proposal && proposal.revision && proposal.revision.resolved_finding_ids) || []);
    setProfileEditSummary('');
  };

  const refreshVariantProfiles = async () => {
    // The registry is expensive: one request per page mount, including after approval.
    // reviewVariantProfile already inserts the approved profile if it is absent here.
    if (!profilesRequest.current) profilesRequest.current = (async () => {
      const API = window.GooseAPI;
      try {
        if (!API || !API.sabreProfiles) throw { status: 404 };
        const data = await API.sabreProfiles();
        const rows = (data && Array.isArray(data.profiles)) ? data.profiles : [];
        setVariantProfiles(rows);
        const view = profilesView(data, client);
        setProfilesState(view);
        setProfileLoadError(view.state === 'error' ? 'Could not load variant profiles.' : null);
        return rows;
      } catch (error) {
        const view = profilesView(error, client);
        setVariantProfiles([]);
        setProfilesState(view);
        setProfileLoadError(view.state === 'unavailable' ? 'Variant profiles are unavailable on this server.' : (error.message || 'Profiles unavailable.'));
        return [];
      }
    })();
    return profilesRequest.current;
  };

  const refreshProfileApprovalQueue = async () => {
    if (!canApproveProfile || !window.GooseAPI || !window.GooseAPI.sabreProfileApprovalQueue) return [];
    try {
      const data = await window.GooseAPI.sabreProfileApprovalQueue();
      const rows = data && Array.isArray(data.proposals) ? data.proposals : [];
      setProfileApprovalQueue(rows);
      return rows;
    } catch (_error) {
      setProfileApprovalQueue([]);
      return [];
    }
  };

  const openProfileApproval = async (requestId) => {
    setProfileError(null); setProfileWorking(true);
    try {
      const proposal = await window.GooseAPI.sabreProfileProposal(requestId);
      adoptProfileDraft(proposal);
      setProfileOnboardingOpen(true);
    } catch (error) {
      setProfileError((error && error.data && error.data.error) || error.message || 'Could not open the submitted profile.');
    } finally { setProfileWorking(false); }
  };

  const proposeVariantProfile = async () => {
    setProfileError(null);
    if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(profileVariantId.trim())) {
      setProfileError('Enter a portable variant ID.'); return;
    }
    if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(profileSubBrandId.trim())) {
      setProfileError('Enter a portable sub-brand ID.'); return;
    }
    const pdfs = profileFiles.filter((file) => /\.pdf$/i.test(file.name));
    const htmls = profileFiles.filter((file) => /\.html?$/i.test(file.name));
    if (pdfs.length !== 1 || htmls.length !== 1) {
      setProfileError(`Choose exactly one design PDF and one shipped HTML; got ${pdfs.length} PDF and ${htmls.length} HTML.`); return;
    }
    const fd = new FormData();
    fd.append('variant_id', profileVariantId.trim());
    fd.append('sub_brand_id', profileSubBrandId.trim());
    profileFiles.forEach((file) => fd.append('files', file));
    setProfileWorking(true);
    try {
      const proposal = await window.GooseAPI.proposeSabreProfile(fd);
      adoptProfileDraft(proposal);
    } catch (error) {
      setProfileError((error && error.data && error.data.error) || error.message || 'Profile analysis failed.');
    } finally { setProfileWorking(false); }
  };

  const updateProfilePolicy = (index, key, value) => {
    setProfileDraftPolicies((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  };

  const addProfilePolicy = () => {
    setProfileDraftPolicies((rows) => [...rows, {
      semantic_role: `new_region_${rows.length + 1}`,
      disposition: 'ship', owner: 'builder', implementation_mode: 'live',
      renderer_recipe: 'live.left.body', responsive_behavior: 'wrap',
      dark_mode_behavior: 'client-default',
    }]);
  };

  const saveProfileDraft = async () => {
    if (!profileProposal || !profileProposal.request_id || !profileEditSummary.trim()) {
      setProfileError('Describe what you confirmed or changed before saving the draft.'); return;
    }
    setProfileError(null); setProfileWorking(true);
    try {
      const result = await window.GooseAPI.amendSabreProfile(profileProposal.request_id, {
        region_policies: profileDraftPolicies,
        resolved_finding_ids: profileResolvedFindings,
        summary: profileEditSummary.trim(),
      });
      adoptProfileDraft(result);
    } catch (error) {
      setProfileError((error && error.data && error.data.error) || error.message || 'Draft update failed.');
    } finally { setProfileWorking(false); }
  };

  const submitProfileDraft = async () => {
    if (!profileProposal || !profileProposal.request_id) return;
    setProfileError(null); setProfileWorking(true);
    try {
      const result = await window.GooseAPI.submitSabreProfile(profileProposal.request_id);
      adoptProfileDraft(result);
    } catch (error) {
      setProfileError((error && error.data && error.data.error) || error.message || 'Profile submission failed.');
    } finally { setProfileWorking(false); }
  };

  const reviewVariantProfile = async (action) => {
    if (!profileProposal || !profileProposal.request_id) return;
    setProfileError(null); setProfileWorking(true);
    try {
      const result = await window.GooseAPI.reviewSabreProfile(profileProposal.request_id, action);
      if (result.status === 'active' && result.profile) {
        const rows = await refreshVariantProfiles();
        setVariantProfileId(result.profile.profile_id);
        if (!rows.some((row) => row.profile_id === result.profile.profile_id)) {
          setVariantProfiles((current) => [...current, result.profile]);
        }
      }
      setProfileProposal({ ...profileProposal, review_status: result.status });
      await refreshProfileApprovalQueue();
    } catch (error) {
      setProfileError((error && error.data && error.data.error) || error.message || 'Profile review failed.');
    } finally { setProfileWorking(false); }
  };

  // Upload a set of File objects as ONE brief folder → a single upload_id the build
  // references. The /upload route makes one folder per POST and can't append, so
  // additional files are added by RE-UPLOADING the source + extras together. No PDF
  // gate here — the caller decides (PDF source only).
  const doUpload = async (fileObjs) => {
    if (!fileObjs.length) { setUploadId(null); setUploadedFiles([]); return null; }
    const fd = new FormData();
    fileObjs.forEach((f) => fd.append('files', f));
    setUploading(true); setUploadError(null);
    try {
      const r = await window.GooseAPI.uploadBrief(fd);
      setUploadId(r.upload_id);
      setUploadedFiles(r.files || []);
      return r;
    } catch (e) {
      setUploadError((e && e.data && e.data.error) || e.message || 'Upload failed.');
      throw e;
    } finally {
      setUploading(false);
    }
  };

  // Source dropzone. PDF source still wants exactly one PDF; HTML/PSD just take the
  // file(s) (HTML-ref needs an .html + change-doc, not a PDF). Replacing the source
  // keeps any already-added extra files (re-uploaded alongside).
  const handleFiles = async (fileList) => {
    setUploadError(null);
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    if (source === 'pdf') {
      const pdfs = picked.filter((f) => /\.pdf$/i.test(f.name));
      if (pdfs.length !== 1) {
        setUploadError(`Pick exactly one PDF as the brief — got ${pdfs.length}.`);
        return;
      }
    } else if (source === 'html') {
      // HTML-ref bundle (ISS-27): exactly one .html template + exactly one change-doc
      // (.docx/.txt/.md telling the swarm WHAT to edit). Mirror the server gate
      // (builds.js) so the user gets instant guidance instead of a post-upload 400 —
      // and a clean slate (no stale "1 file ready" left beside the error). Drop both.
      const htmls = picked.filter((f) => /\.html?$/i.test(f.name));
      const docs = picked.filter((f) => /\.(docx|txt|md)$/i.test(f.name));
      if (htmls.length !== 1) {
        setUploadId(null); setUploadedFiles([]); setSourceFiles([]);
        setUploadError(`HTML-ref needs exactly one .html template — got ${htmls.length}.`);
        return;
      }
      if (docs.length !== 1) {
        setUploadId(null); setUploadedFiles([]); setSourceFiles([]);
        setUploadError(`HTML-ref also needs a change-doc (.docx / .txt / .md) describing the edits — drop it together with the .html. Got ${docs.length}.`);
        return;
      }
    }
    setSourceFiles(picked);
    try { await doUpload([...picked, ...extraFiles]); }
    catch (e) { setSourceFiles([]); }
  };

  // "Add files" → REAL upload of additional files alongside the source (brand
  // guidelines, reference HTML, image assets, legal copy). Re-uploads the whole set
  // so they land in the same brief folder the swarm reads.
  const handleExtraFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const next = [...extraFiles, ...picked];
    try { await doUpload([...sourceFiles, ...next]); setExtraFiles(next); }
    catch (e) { /* uploadError already surfaced; keep prior state */ }
  };
  const removeExtra = async (i) => {
    const next = extraFiles.filter((_, j) => j !== i);
    try { await doUpload([...sourceFiles, ...next]); setExtraFiles(next); }
    catch (e) { /* keep prior state */ }
  };

  // Easter egg: tap the step dots 1‑5‑4‑1‑1 to slip into the Cockpit.
  const EGG = '15411';
  const eggRef = React.useRef('');
  const [eggHot, setEggHot] = React.useState(false);
  const tapStep = (n) => {
    const seq = (eggRef.current + n).slice(-EGG.length);
    eggRef.current = seq;
    setEggHot(EGG.startsWith(seq) && seq.length > 1);
    if (seq === EGG) {
      eggRef.current = '';
      setEggHot(false);
      window.location.href = 'Goose Cockpit - Build Order.html';
    }
  };

  const sources = [
    { id: 'pdf', label: 'PDF', ico: I.pdf, desc: 'Design export from Figma / InDesign' },
    { id: 'figma', label: 'Figma', ico: I.figma, desc: 'Live frame via REST token' },
    { id: 'html', label: 'HTML ref', ico: I.code, desc: 'Add a known-good template' },
    { id: 'psd', label: 'PSD', ico: I.psd, desc: 'Photoshop file', disabled: true },
  ];
  const tiers = [
    { id: 'economy', label: 'Economy', dots: 1, desc: 'Gemini + DeepSeek', cost: '$0.08–0.14', soon: true },
    { id: 'balanced', label: 'Balanced', dots: 2, desc: 'Sonnet semantic + Gemini', cost: '$0.18–0.32' },
    { id: 'max', label: 'Max quality', dots: 4, desc: 'Opus orchestrator', cost: '$1.20–1.71', admin: true, soon: true },
    { id: 'wingman', label: 'Wingman', dots: 4, desc: 'Custom tuned for maximum accuracy', cost: '$1.40–1.95', soon: true },
  ];

  const STD = { name: 'Standard email HTML', width: 600, font: 'system sans', refs: 0, flags: ['No client profile', 'Universal rules only'], presets: [], colors: [] };
  const c = standard ? STD : window.MAV.clientBySlug(client);
  const tierObj = tiers.find(t => t.id === tier);
  // ORG-02: sme/dev see their REAL assignments (server truth, and the server enforces at
  // /start regardless — a hidden option is never security); others keep the team mock/all.
  const scopedClients = isClientScoped
    ? clients.filter(cl => myAssigned.includes(cl.slug))
    : (scope === 'team' && mockTeamScope) ? clients.filter(cl => myTeam.clientSlugs.includes(cl.slug)) : clients;
  const shown = scopedClients.filter(cl => cl.name.toLowerCase().includes(search.toLowerCase()));
  // keep the selection valid when the scope narrows
  React.useEffect(() => {
    if (standard) { setAutoDetect(false); setVariantProfileId(''); return; }
    if (isClientScoped) {
      if (myAssigned.length && !myAssigned.includes(client)) setClient(myAssigned[0]);
    } else if (scope === 'team' && mockTeamScope && !myTeam.clientSlugs.includes(client)) setClient(myTeam.clientSlugs[0]);
  }, [scope]);
  // presets are per-client — reset to Standard whenever the client changes
  React.useEffect(() => { setPreset('standard'); setVariantProfileId(''); }, [client]);
  React.useEffect(() => { if (autoDetect) setVariantProfileId(''); }, [autoDetect]);
  const variantsView = variantProfiles.length || ['ready', 'none'].includes(profilesState.state)
    ? profilesView({ profiles: variantProfiles, degraded_profiles: profilesState.degraded }, client) : profilesState;
  const variantHues = clientHues(variantsView.ready.map(profile => ({ id: profile.profile_id, status: 'ready' })));
  const selectedVariant = variantsView.ready.find(profile => profile.profile_id === variantProfileId);
  const selectedHue = variantHues[variantProfileId] || byKey('');
  const presets = (c && c.presets) || [];
  const presetObj = presets.find(p => p.id === preset);
  const sabreEligible = !standard && client === 'synchrony' && source === 'pdf'
    && preset === 'standard' && !!uploadId;

  // Fetch live pre-flight data once on mount (kept off the global hydrate so it
  // doesn't slow login). Graceful: any failure leaves state null → static UI.
  React.useEffect(() => {
    let alive = true;
    const API = window.GooseAPI;
    if (API && API.tierCosts) API.tierCosts().then(d => { if (alive) setTierCostData(d && d.tiers ? d.tiers : {}); }).catch(() => {});
    if (API && API.openrouterCredits) API.openrouterCredits().then(d => { if (alive) setCredits(d || null); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  React.useEffect(() => { refreshVariantProfiles(); refreshProfileApprovalQueue(); }, []);

  // Est. cost: average of the SELECTED tier's recent builds when we have real
  // data for it, else the static range from the tiers table. estCostNum is the
  // numeric figure used for the "budget after" subtraction.
  const liveTier = tierCostData && tierCostData[tier];
  const liveCostReady = !!(liveTier && liveTier.count > 0 && liveTier.avg != null);
  const estCostLabel = liveCostReady ? `~$${liveTier.avg.toFixed(2)}` : tierObj.cost;
  const estCostTitle = liveCostReady
    ? `Average of ${liveTier.count} ${tierObj.label} build${liveTier.count === 1 ? '' : 's'} ($${liveTier.min.toFixed(2)}–$${liveTier.max.toFixed(2)})`
    : `Estimate — no ${tierObj.label} builds tracked yet`;
  const estCostNum = liveCostReady ? liveTier.avg : parseCostMid(tierObj.cost);

  // Budget after: live OpenRouter balance minus this build's est cost.
  const budgetAvail = !!(credits && credits.available && credits.remaining != null);
  const budgetAfterLabel = budgetAvail ? `$${Math.max(0, credits.remaining - estCostNum).toFixed(2)} left` : '—';
  const budgetAfterTitle = budgetAvail
    ? `OpenRouter balance $${credits.remaining.toFixed(2)} − est. $${estCostNum.toFixed(2)} this build`
    : `OpenRouter balance unavailable${credits && credits.detail ? ' — ' + credits.detail : ''}`;

  return (
    <div className="page fade-in nb2">
      <div className="page-head">
        <div>
          <div className="row gap-12 wrap" style={{ marginBottom: 4 }}>
            {['Client', 'ESP', 'Source', 'Upload', 'Configure'].map((s, i) => (
              <div key={s} className={'wiz-step' + (i === 0 ? ' active' : '') + (eggHot ? ' egg-armed' : '')}>
                <button type="button" className="wiz-num" onClick={() => tapStep(i + 1)} title={s} aria-label={'Step ' + (i + 1) + ': ' + s}>{i + 1}</button>
                <span style={{ fontSize: 13, fontWeight: 600, color: i === 0 ? 'var(--ink)' : 'var(--muted)' }}>{s}</span>
                {i < 4 && <span style={{ color: 'var(--faint)', marginLeft: 4 }}>·</span>}
              </div>
            ))}
          </div>
          <h1 className="page-title" style={{ marginTop: 10 }}>New build</h1>
        </div>
        <button className="btn btn-quiet" onClick={() => go('home')}><I.x size={15} /> Cancel</button>
      </div>

      {savedStarts.length > 0 && <div className="card card-pad mb-20" data-saved-build-requests>
        <div className="sec-title">Saved submissions</div>
        <p className="muted">These are receipts, not another build. Check an uncertain submission before uploading or starting again. Checking status does not run a model.</p>
        {savedStarts.map(saved => <div className="row gap-12 wrap mt-10" key={saved.key}>
          <span className="grow">{saved.name} · {saved.job_id || 'Awaiting job confirmation'} · {saved.dispatch_status || 'unconfirmed'}</span>
          <button type="button" className="btn btn-quiet" disabled={!!checkingStart || starting} onClick={async () => {
            const key = window.MAV.__contextKey, epoch = window.MAV.__contextEpoch;
            setCheckingStart(saved.key); setStartError(null);
            try {
              const result = await window.GooseAPI.checkBuildRequest(saved.key);
              if (window.MAV.__contextKey !== key || window.MAV.__contextEpoch !== epoch) return;
              refreshSavedStarts();
              if (result.found && result.job_id && typeof openJob === 'function') openJob(result.job_id);
              else setStartError('No committed job is visible for this request yet. If the original form is still available, retry it unchanged; otherwise keep this receipt while recovery is checked.');
            } catch (error) {
              if (window.MAV.__contextKey === key && window.MAV.__contextEpoch === epoch) setStartError(error.message);
            } finally {
              if (window.MAV.__contextKey === key && window.MAV.__contextEpoch === epoch) setCheckingStart(null);
            }
          }}>Check / open original job</button>
          {saved.pending === false && saved.dispatch_status === 'published' && <button type="button" className="btn btn-quiet" disabled={starting || !!checkingStart} onClick={() => {
            window.GooseAPI.newBuildIntent(saved.key); refreshSavedStarts();
          }}>Allow a new build with these inputs</button>}
        </div>)}
      </div>}

      <div className="grid-hero">
        <div className="col gap-20">

          {/* 1. Client */}
          <div className="card card-pad">
            <div className="sec-head">
              <span className="sec-title"><span className="mono muted" style={{ fontSize: 12, marginRight: 8 }}>01</span>Client</span>
              <button className={'chip' + (autoDetect ? '' : '')} onClick={() => {
                if (standard) setScope(scopeOptions[0].k);
                if (!autoDetect) setVariantProfileId('');
                setAutoDetect(!autoDetect);
              }}
                style={autoDetect ? { background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)' } : {}}>
                <I.bolt size={12} /> Auto-detect from file
              </button>
            </div>
            <div className="scope-bar">
              <div className="sc2" role="radiogroup" aria-label="Build scope" data-scope={scope}
                style={{ '--i': scopeOptions.findIndex(o => o.k === scope), '--n': scopeOptions.length }}>
                <span className="sc2-puck" aria-hidden="true"></span>
                {scopeOptions.map(o => (
                  <button key={o.k} type="button" role="radio" aria-checked={scope === o.k}
                    aria-label={o.l + (o.n == null ? '' : ' (' + (o.count || o.n) + ')')}
                    className={'sc2-opt' + (scope === o.k ? ' on' : '')} onClick={() => {
                      setScope(o.k);
                      if (o.k === 'standard') { setAutoDetect(false); setVariantProfileId(''); }
                    }}
                    title={o.k === 'standard' ? 'A generic email with no client profile, variant or brand rules' : undefined}>
                    <span className="sc2-ico"><o.ico size={15} /></span>
                    <span className="sc2-t"><b>{o.l}</b><i>{o.s}</i></span>
                    {o.n != null && <span className="sc2-n">{o.n}</span>}
                  </button>
                ))}
              </div>
            </div>
            {standard ? (
              <React.Fragment>
                <div className="std-card">
                  <span className="std-ico"><I.doc size={18} /></span>
                  <div style={{ minWidth: 0 }}>
                    <b>Standard email HTML</b>
                    <p>A clean, client-agnostic build: table-based, inline styles and dark-mode safe. No brand profile, no recorded variant and no presets are applied — the engine works only from your source file and its universal email rules.</p>
                    <ul>
                      <li>Good for one-off sends, prototypes and clients not yet onboarded</li>
                      <li>ESP targeting remains SFMC for V1</li>
                    </ul>
                  </div>
                </div>
                <StdLayout value={stdLayout} onChange={setStdLayout} clamp={clampLayout} />
              </React.Fragment>
            ) : !autoDetect ? (
              <React.Fragment>
                <div className="searchbox" style={{ width: '100%', marginBottom: 12 }}>
                  <I.search size={15} />
                  <input placeholder={isClientScoped ? 'Search your assigned clients…' : scope === 'team' && myTeam ? 'Search ' + myTeam.name + ' clients…' : 'Search all client profiles…'} value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="client-list">
                  {shown.map(cl => {
                    const st = window.MAV.clientBuildStats(cl.slug);
                    const wk = st.week;
                    return (
                    <button key={cl.slug} className={'client-opt' + (client === cl.slug ? ' sel' : '')} onClick={() => { if (client !== cl.slug) setVariantProfileId(''); setClient(cl.slug); }}>
                      <ClientBadge slug={cl.slug} size={34} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{cl.name}</div>
                        <Swatches colors={cl.colors} />
                        {!st.unavailable && <span className="client-ships" role="button" tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); setStatsClient(cl.slug); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setStatsClient(cl.slug); } }}
                          title="View build tracking for this client">
                          {wk} {wk === 1 ? 'build' : 'builds'} shipped this past week
                        </span>}
                      </div>
                    </button>
                    );
                  })}
                  {shown.length === 0 && <div className="muted" style={{ fontSize: 12.5, padding: '14px 4px', gridColumn: '1 / -1' }}>No {scope === 'team' && myTeam ? myTeam.name + ' ' : ''}clients match “{search}”.</div>}
                </div>
                {isClientScoped ? (
                  <p className="muted" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.5 }}><I.lock size={11} style={{ verticalAlign: '-1px', marginRight: 3 }} />{myAssigned.length ? <React.Fragment>You're seeing your <b>{myAssigned.length}</b> assigned {myAssigned.length === 1 ? 'client' : 'clients'}. Ask your team lead to assign more.</React.Fragment> : <React.Fragment>No clients are assigned to you yet — ask your team lead for an assignment before starting a build.</React.Fragment>}</p>
                ) : scope === 'team' && mockTeamScope && (
                  <p className="muted" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.5 }}><I.lock size={11} style={{ verticalAlign: '-1px', marginRight: 3 }} />You're seeing the clients assigned to <b>{myTeam.name}</b>. Switch to “All clients” for cross-team builds.</p>
                )}
              </React.Fragment>
            ) : (
              <div className="dropzone" style={{ padding: 22 }}>
                <I.bolt size={22} />
                <p style={{ fontSize: 13, fontWeight: 600, margin: '8px 0 2px' }}>The engine will detect the client during planning</p>
                <p className="muted" style={{ fontSize: 12, margin: 0 }}>Low-confidence detections route to the review queue.</p>
              </div>
            )}

            {!autoDetect && !standard && c && (
              <ClientVariants clientName={c.name} clientSlug={client} view={variantsView}
                pendingCount={canApproveProfile ? profileApprovalQueue.filter(row => row.scope && row.scope.client_id === client).length : null}
                valueId={variantProfileId} onSelect={setVariantProfileId} applies={agentObj.engine === 'sabre'}
                hues={clientHues} byKey={byKey} />
            )}
          </div>

          {/* Email Service Provider */}
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title"><span className="mono muted" style={{ fontSize: 12, marginRight: 8 }}>02</span>Email Service Provider</span></div>
            <EspStep esp={espSummary} onEsp={setEspSummary} onEspDetail={setEspDetail} />
            {!SERVER_ACCEPTS.esps
              ? <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }} data-esp-wire="held">Selection is UI-only for now — the build server still ships to Salesforce Marketing Cloud (V1).</p>
              : <span data-esp-wire="sent" />}
          </div>

          {/* 2. Source type */}
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title"><span className="mono muted" style={{ fontSize: 12, marginRight: 8 }}>03</span>Source type</span></div>
            <div className="seg">
              {sources.map(s => (
                <button key={s.id} className={'seg-opt' + (source === s.id ? ' sel' : '') + (s.disabled ? ' disabled' : '')}
                  onClick={() => !s.disabled && setSource(s.id)} disabled={s.disabled}>
                  {s.disabled && <span className="soon">Soon</span>}
                  <div className="si"><s.ico size={26} /></div>
                  <div className="sl">{s.label}</div>
                  <div className="sd">{s.desc}</div>
                </button>
              ))}
            </div>
            {agent === 'sabre' && (
              <p className="muted" style={{ fontSize: 11.5, marginTop: 9 }} data-sabre-scope>
                <I.lock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                MVP scope: Synchrony · PDF · Standard · uploaded bundle. Sabre writes a Decision Sheet; the deterministic builder writes HTML.
              </p>
            )}
          </div>

          {/* 3. Upload */}
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title"><span className="mono muted" style={{ fontSize: 12, marginRight: 8 }}>04</span>Upload</span><span className="chip">Batch ready</span></div>
            {source === 'figma' ? (
              <div>
                <label className="field-lbl">Figma frame URL</label>
                <input className="input" placeholder="https://www.figma.com/file/…" value={figmaUrl} onChange={(e) => setFigmaUrl(e.target.value)} />
                <p className="muted mt-10" style={{ fontSize: 12 }}>Uses your saved Figma REST token · <a href="#" style={{ color: 'var(--brand-orange)', fontWeight: 600 }}>Manage in integrations</a></p>
              </div>
            ) : (
              <React.Fragment>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.txt,.png,.jpg,.jpeg,.gif,.html,.htm,.docx,.md"
                  style={{ display: 'none' }}
                  onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                />
                <div
                  className="dropzone"
                  data-upload-status={uploadId ? 'uploaded' : 'empty'}
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current && fileInputRef.current.click(); } }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFiles(e.dataTransfer.files); }}
                  style={{ cursor: 'pointer' }}
                >
                  <I.download size={26} />
                  {uploading ? (
                    <p style={{ fontSize: 14, fontWeight: 600, margin: '10px 0 2px' }}>Uploading…</p>
                  ) : uploadId ? (
                    <React.Fragment>
                      <p style={{ fontSize: 14, fontWeight: 600, margin: '10px 0 2px' }}>{uploadedFiles.length} file{uploadedFiles.length === 1 ? '' : 's'} ready</p>
                      <p className="muted" style={{ fontSize: 12, margin: 0 }}>upload <span className="mono">{uploadId}</span> · click to replace</p>
                    </React.Fragment>
                  ) : source === 'html' ? (
                    <React.Fragment>
                      <p style={{ fontSize: 14, fontWeight: 600, margin: '10px 0 2px' }}>Drop your HTML template + a change-doc</p>
                      <p className="muted" style={{ fontSize: 12, margin: 0 }}>one <span className="mono">.html</span> · plus a <span className="mono">.docx / .txt / .md</span> describing the edits to make</p>
                      <p className="muted" style={{ fontSize: 11, margin: '8px 0 0' }}>Drop both together · extra image assets are optional</p>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <p style={{ fontSize: 14, fontWeight: 600, margin: '10px 0 2px' }}>Drop your design PDF here</p>
                      <p className="muted" style={{ fontSize: 12, margin: 0 }}>or click to browse · .pdf, requirements .txt, image assets</p>
                      <p className="muted" style={{ fontSize: 11, margin: '8px 0 0' }}>No upload → falls back to bundled <span className="mono">pipeline_demo_1</span></p>
                    </React.Fragment>
                  )}
                </div>
                {uploadError && <p style={{ fontSize: 12, color: 'var(--fail)', marginTop: 8 }} role="alert">{uploadError}</p>}
                {uploadedFiles.length > 0 && (
                  <div className="row gap-8 wrap mt-16">
                    {uploadedFiles.map((f, i) => {
                      const Ico = f.kind === 'pdf' ? I.pdf : (f.kind === 'txt' ? I.doc : I.image);
                      const sizeKb = f.size > 0 ? (f.size > 1024 * 1024 ? (f.size / 1024 / 1024).toFixed(1) + ' MB' : Math.round(f.size / 1024) + ' KB') : '';
                      return <span key={i} className="chip"><Ico size={13} /> {f.name.length > 36 ? f.name.slice(0, 36) + '…' : f.name} {sizeKb && <span className="muted">· {sizeKb}</span>}</span>;
                    })}
                  </div>
                )}
              </React.Fragment>
            )}
            <div className="addl mt-20">
              <input ref={extraInputRef} type="file" multiple
                accept=".pdf,.txt,.png,.jpg,.jpeg,.gif,.html,.htm,.docx,.md"
                style={{ display: 'none' }}
                onChange={(e) => { handleExtraFiles(e.target.files); e.target.value = ''; }} />
              <div className="row between" style={{ marginBottom: 8 }}>
                <span className="field-lbl mb-0">Additional files <span className="muted" style={{ fontWeight: 500 }}>· optional</span></span>
                <button className="chip" style={{ cursor: uploading ? 'default' : 'pointer' }} disabled={uploading}
                  onClick={() => extraInputRef.current && extraInputRef.current.click()}><I.add size={12} /> {uploading ? 'Uploading…' : 'Add files'}</button>
              </div>
              <p className="muted" style={{ fontSize: 11.5, margin: '0 0 11px' }}>Brand guidelines, extra image assets, reference HTML, legal copy — uploaded into the brief alongside the source.</p>
              {extraFiles.length ? (
                <div className="row gap-8 wrap">
                  {extraFiles.map((f, i) => (
                    <span key={i} className="chip">{f.name.length > 32 ? f.name.slice(0, 32) + '…' : f.name}<button onClick={() => removeExtra(i)} style={{ display: 'inline-flex', marginLeft: 5, color: 'var(--faint)' }} title="Remove"><I.x size={11} /></button></span>
                  ))}
                </div>
              ) : <div className="muted" style={{ fontSize: 12 }}>No additional files added.</div>}
            </div>
          </div>

          {/* 4. Configure */}
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title"><span className="mono muted" style={{ fontSize: 12, marginRight: 8 }}>05</span>Configuration</span></div>
            <label className="field-lbl">Build name / test-id</label>
            <input className="input" value={buildName} onChange={(e) => setBuildName(e.target.value)} pattern="[A-Za-z0-9_-]+" title="Letters, numbers, _ and - only" />
            <label className="field-lbl" style={{ marginTop: 16 }}>Build agent</label>
            <div className="seg" data-agent-tiles>
              {agents.map(a => (
                <button type="button" key={a.id}
                  className={'seg-opt' + (agent === a.id ? ' sel' : '') + (a.soon ? ' soon-tile' : '') + (a.preview ? ' preview' : (a.soon ? ' disabled' : ''))}
                  onClick={() => (!a.soon || a.preview) && setAgent(a.id)} disabled={a.soon && !a.preview}
                  aria-pressed={agent === a.id}>
                  {a.soon && <span className="soon">Soon</span>}
                  <div className="si" data-agent-icon={a.ico || 'terminal'}>{React.createElement(I[a.ico] || I.terminal, { size: 26 })}</div>
                  <div className="sl">{a.label}</div>
                  <div className="sd">{a.desc}</div>
                </button>
              ))}
            </div>
            {agentObj.devtest && <p className="muted" style={{ fontSize: 11.5, marginTop: 9 }}><I.lock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />Agentic builds run as <b>internal devtest</b> (operator-attended). More agents come online as their launchers land.</p>}
            {showModelTier && (
            <React.Fragment>
            <label className="field-lbl" style={{ marginTop: 16 }}>Model tier</label>
            <div className="col gap-10">
              {tiers.map(t => (
                <button key={t.id} className={'tier-opt' + (tier === t.id ? ' sel' : '')} onClick={() => !t.soon && setTier(t.id)} disabled={t.soon} style={t.soon ? { opacity: 0.5, cursor: 'not-allowed' } : null} title={t.soon ? 'V1 runs the Balanced swarm — other tiers are coming soon' : null}>
                  <div className="row between">
                    <div className="row gap-10">
                      <span style={{ fontSize: 13.5, fontWeight: 700 }}>{t.label}</span>
                      {t.soon ? <SoonChip /> : (t.admin && <span className="chip" style={{ fontSize: 10 }}>Admin only</span>)}
                    </div>
                    <span className="cost-dots">{[0,1,2,3].map(i => <i key={i} className={i < t.dots ? 'on' : ''}></i>)}</span>
                  </div>
                  <div className="row between mt-6">
                    <span className="muted" style={{ fontSize: 12 }}>{t.desc}</span>
                    <span className="mono muted" style={{ fontSize: 11.5 }}>{t.cost}</span>
                  </div>
                </button>
              ))}
            </div>
            </React.Fragment>
            )}
            {agent === 'sabre' ? (
              <div className="col gap-12 mt-16" data-sabre-profile-panel>
                <div className="row gap-10" style={{ padding: 12, border: '1px solid var(--line)', borderRadius: 8 }}>
                  <I.lock size={15} />
                  <div><b style={{ fontSize: 12 }}>Reference selection is authority-bound</b><div className="muted" style={{ fontSize: 11.5 }}>Sabre scores only approved, dev-shipped Synchrony references and records every candidate.</div></div>
                </div>
                <div style={{ padding: 12, border: '1px solid var(--line)', borderRadius: 8 }}>
                  <div className="row between gap-12 wrap">
                    <div className="grow">
                      <label className="field-lbl">Variant profile</label>
                      <select className="input" aria-label="Sabre variant profile" value={variantProfileId}
                        onChange={(event) => setVariantProfileId(event.target.value)}>
                        <option value="">Synchrony Standard · built-in</option>
                        {variantProfiles.map((profile) => (
                          <option key={profile.profile_id} value={profile.profile_id}>
                            {profile.scope.variant_id} · {profile.container_width}px · approved
                          </option>
                        ))}
                      </select>
                    </div>
                    <button type="button" className="btn btn-quiet" data-action="onboard-sabre-profile"
                      onClick={() => setProfileOnboardingOpen((open) => !open)}>
                      <I.add size={13} /> {profileOnboardingOpen ? 'Close onboarding' : 'Onboard developer gold'}
                    </button>
                  </div>
                  {profileLoadError && <p role="alert" style={{ color: 'var(--fail)', fontSize: 11.5, marginTop: 8 }}>{profileLoadError}</p>}
                  {profileOnboardingOpen && (
                    <div className="col gap-10 mt-16" data-sabre-profile-onboarding style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                      {canApproveProfile && profileApprovalQueue.length > 0 && (
                        <div className="col gap-6" data-profile-approval-queue style={{ padding: 10, border: '1px solid var(--line)', borderRadius: 7 }}>
                          <b style={{ fontSize: 11.5 }}>Workspace owner queue</b>
                          {profileApprovalQueue.map((row) => (
                            <button key={row.request_id} type="button" className="btn btn-quiet" disabled={profileWorking} onClick={() => openProfileApproval(row.request_id)}>
                              {row.scope.variant_id} · submitted by {row.submitted_by.replace(/^developer:/, '')}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="row gap-10 wrap">
                        <div className="grow"><label className="field-lbl">Variant ID</label><input className="input" aria-label="Profile variant ID" value={profileVariantId} onChange={(event) => setProfileVariantId(event.target.value)} placeholder="flr" /></div>
                        <div className="grow"><label className="field-lbl">Sub-brand ID</label><input className="input" aria-label="Profile sub-brand ID" value={profileSubBrandId} onChange={(event) => setProfileSubBrandId(event.target.value)} placeholder="synchrony-home" /></div>
                      </div>
                      <input ref={profileInputRef} type="file" multiple style={{ display: 'none' }}
                        accept=".pdf,.html,.htm,.txt,.md,.csv,.json,.docx,.gif,.png,.jpg,.jpeg,.webp"
                        onChange={(event) => { setProfileFiles(Array.from(event.target.files || [])); setProfileProposal(null); setProfileError(null); event.target.value = ''; }} />
                      <button type="button" className="btn btn-quiet" data-action="choose-profile-files"
                        onClick={() => profileInputRef.current && profileInputRef.current.click()}>
                        <I.download size={13} /> {profileFiles.length ? `${profileFiles.length} onboarding files selected` : 'Choose design + shipped code'}
                      </button>
                      <p className="muted" style={{ fontSize: 11.5, margin: 0 }}>Exactly one design PDF and one developer-shipped HTML; specs and supplied assets are optional evidence.</p>
                      <button type="button" className="btn btn-primary" data-action="analyze-sabre-profile" disabled={profileWorking || !profileFiles.length}
                        onClick={proposeVariantProfile}>{profileWorking ? 'Analyzing…' : 'Analyze profile'}</button>
                      {profileError && <p role="alert" data-profile-error style={{ color: 'var(--fail)', fontSize: 11.5, margin: 0 }}>{profileError}</p>}
                      {profileProposal && (
                        <div data-profile-proposal style={{ padding: 11, background: 'var(--panel, rgba(0,0,0,.025))', borderRadius: 7 }}>
                          <div className="row between gap-8 wrap">
                            <b style={{ fontSize: 12 }}>{profileProposal.workflow_status === 'submitted' ? 'Submitted · awaiting workspace owner' : (profileProposal.reasoning && profileProposal.reasoning.status === 'needs_input' ? 'AI draft · developer confirmation required' : 'AI draft ready for developer review')}</b>
                            <span className="chip mono" style={{ fontSize: 10 }}>{profileProposal.profile_id || profileProposal.proposal_id}</span>
                          </div>
                          <div className="muted" style={{ fontSize: 11.5, marginTop: 7 }}>
                            {profileProposal.decision_rows.length} evidence-bound decisions · {profileProposal.candidate_profile ? `${profileProposal.candidate_profile.container_width}px shell` : 'no profile emitted'}
                          </div>
                          {profileProposal.reasoning && (
                            <details style={{ marginTop: 10 }}>
                              <summary style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 650 }}>
                                What Sabre inferred · {profileProposal.reasoning.model_route} / {profileProposal.reasoning.model_version}
                              </summary>
                              <div className="col gap-6 mt-10">
                                {profileProposal.reasoning.decisions.map((decision) => (
                                  <div key={decision.decision_id} style={{ borderLeft: '2px solid var(--line)', paddingLeft: 8, fontSize: 11 }}>
                                    <b>{decision.semantic_role}</b> · {decision.verdict} · {decision.derivation_class}
                                    <div className="muted">{decision.reason}</div>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                          {profileProposal.dialect && profileProposal.dialect.candidate && (
                            <details style={{ marginTop: 8 }} data-profile-dialect>
                              <summary style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 650 }}>
                                Inferred build dialect · {profileProposal.dialect.candidate.class_prefix || 'no prefix'} · {profileProposal.dialect.candidate.shell.mobile_breakpoint}px breakpoint
                              </summary>
                              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                                {profileProposal.dialect.candidate.styles.length} typed section styles · {profileProposal.dialect.candidate.shell.main_table_class} shell · {profileProposal.dialect.candidate.shell.compact_horizontal_padding}px compact gutters
                              </div>
                            </details>
                          )}
                          {profileProposal.terminal_findings.length > 0 && (
                            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 11.5 }}>
                              {profileProposal.terminal_findings.map((finding) => <li key={finding}>{finding}</li>)}
                            </ul>
                          )}
                          {profileProposal.reasoning && profileProposal.reasoning.findings.length > 0 && (
                            <div className="col gap-7 mt-10" data-profile-findings>
                              {profileProposal.reasoning.findings.map((finding) => (
                                <label key={finding.finding_id} className="row gap-8" style={{ alignItems: 'flex-start', fontSize: 11.5 }}>
                                  <input type="checkbox" checked={profileResolvedFindings.includes(finding.finding_id)}
                                    disabled={profileProposal.workflow_status === 'submitted'}
                                    onChange={(event) => setProfileResolvedFindings((current) => event.target.checked
                                      ? [...new Set([...current, finding.finding_id])]
                                      : current.filter((id) => id !== finding.finding_id))} />
                                  <span><b>{finding.blocking ? 'Must resolve' : 'Review'}:</b> {finding.message}<br /><span className="muted">{finding.correction_required}</span></span>
                                </label>
                              ))}
                            </div>
                          )}
                          {profileProposal.candidate_profile && profileProposal.workflow_status !== 'submitted' && (
                            <div className="col gap-8 mt-10" data-profile-rule-editor>
                              {profileDraftPolicies.map((policy, index) => (
                                <div key={`${policy.semantic_role}-${index}`} style={{ padding: 9, border: '1px solid var(--line)', borderRadius: 6 }}>
                                  <div className="row gap-7 wrap">
                                    <input className="input grow" aria-label={`Rule ${index + 1} semantic role`} value={policy.semantic_role} onChange={(event) => updateProfilePolicy(index, 'semantic_role', event.target.value)} />
                                    <select className="input" aria-label={`Rule ${index + 1} disposition`} value={policy.disposition} onChange={(event) => updateProfilePolicy(index, 'disposition', event.target.value)}>
                                      {['ship', 'omit', 'metadata', 'inject'].map((value) => <option key={value}>{value}</option>)}
                                    </select>
                                    <select className="input" aria-label={`Rule ${index + 1} owner`} value={policy.owner} onChange={(event) => updateProfilePolicy(index, 'owner', event.target.value)}>
                                      {['builder', 'metadata', 'esp', 'none'].map((value) => <option key={value}>{value}</option>)}
                                    </select>
                                    <button type="button" className="btn btn-quiet" onClick={() => setProfileDraftPolicies((rows) => rows.filter((_row, rowIndex) => rowIndex !== index))}>Remove</button>
                                  </div>
                                  <div className="row gap-7 wrap mt-10">
                                    <select className="input grow" aria-label={`Rule ${index + 1} implementation mode`} value={policy.implementation_mode || ''} onChange={(event) => updateProfilePolicy(index, 'implementation_mode', event.target.value || null)}>
                                      <option value="">none</option>{['live', 'image', 'background', 'hybrid'].map((value) => <option key={value}>{value}</option>)}
                                    </select>
                                    <select className="input grow" aria-label={`Rule ${index + 1} recipe`} value={policy.renderer_recipe} onChange={(event) => updateProfilePolicy(index, 'renderer_recipe', event.target.value)}>
                                      {SABRE_PROFILE_RECIPES.map((value) => <option key={value}>{value}</option>)}
                                    </select>
                                    <input className="input grow" aria-label={`Rule ${index + 1} responsive behavior`} value={policy.responsive_behavior} onChange={(event) => updateProfilePolicy(index, 'responsive_behavior', event.target.value)} placeholder="responsive behavior" />
                                    <input className="input grow" aria-label={`Rule ${index + 1} dark mode behavior`} value={policy.dark_mode_behavior} onChange={(event) => updateProfilePolicy(index, 'dark_mode_behavior', event.target.value)} placeholder="dark-mode behavior" />
                                  </div>
                                </div>
                              ))}
                              <button type="button" className="btn btn-quiet" onClick={addProfilePolicy}><I.add size={13} /> Add rule</button>
                              <textarea className="input" rows={2} aria-label="Profile edit summary" value={profileEditSummary} onChange={(event) => setProfileEditSummary(event.target.value)} placeholder="What did you confirm, add, remove, or change?" />
                              <div className="row gap-8 wrap">
                                <button type="button" className="btn btn-quiet" data-action="save-sabre-profile-draft" disabled={profileWorking || !profileDraftPolicies.length} onClick={saveProfileDraft}>Save reviewed draft</button>
                                <button type="button" className="btn btn-primary" data-action="submit-sabre-profile" disabled={profileWorking || !profileDraftPolicies.length} onClick={submitProfileDraft}><I.check size={13} /> Send for owner approval</button>
                              </div>
                            </div>
                          )}
                          {profileProposal.workflow_status === 'submitted' && !profileProposal.review_status && (
                            canApproveProfile ? (
                              <div className="row gap-8 mt-10">
                                <button type="button" className="btn btn-primary" data-action="approve-sabre-profile" disabled={profileWorking} onClick={() => reviewVariantProfile('approve')}><I.check size={13} /> Approve as workspace owner</button>
                                <button type="button" className="btn btn-quiet" data-action="reject-sabre-profile" disabled={profileWorking} onClick={() => reviewVariantProfile('reject')}>Reject variant</button>
                              </div>
                            ) : <p className="muted" style={{ fontSize: 11.5, margin: '8px 0 0' }}>This exact draft is locked for an independent workspace/client owner.</p>
                          )}
                          {profileProposal.review_status && <p data-profile-review-status style={{ color: profileProposal.review_status === 'active' ? 'var(--pass)' : 'var(--fail)', fontSize: 11.5, margin: '8px 0 0' }}>Review recorded: {profileProposal.review_status}.</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : <div className="row gap-12 mt-16 wrap">
              <div className="grow"><label className="field-lbl">Reference override</label><select className="input"><option>Auto (KB best match)</option><option>10511452_SYF_FLR_0162</option><option>14730053_SYF_HOM_1221</option></select></div>
              <div style={{ width: 130 }}><label className="field-lbl">Spacer pairs</label><input className="input" defaultValue="112" /></div>
            </div>}
            <label className="field-lbl" style={{ marginTop: 16 }}>Custom instructions for the squadron</label>
            <textarea className="input" rows={3} placeholder="e.g. Force single-column on mobile · keep hero under 600 KB · use legal disclaimer v3 · image-based CTAs only · GEN code bottom-right" value={instr} onChange={e => setInstr(e.target.value)}></textarea>
            <p className="muted" style={{ fontSize: 11.5, marginTop: 7 }}>{agent === 'sabre'
              ? 'Treated as untrusted intake evidence; only validated decisions reach the BuildContract.'
              : 'Fed verbatim into the planning & execution stages.'}</p>
            <div className={'ab-toggle-row mt-16' + (afterburnerOn ? ' on' : '')}>
              <div className="ab-toggle-info">
                <span className="ab-flame"><I.flame size={17} /></span>
                <div className="ab-toggle-text">
                  <div className="ab-toggle-title">Afterburner QA <span className="chip" style={{ fontSize: 10 }}>Optional</span></div>
                  <div className="muted ab-toggle-desc">Cross-client render diffing via Email on Acid, run after main QA. Off by default.</div>
                </div>
              </div>
              <button type="button" className={'switch' + (afterburnerOn ? ' on' : '')} onClick={() => setAfterburnerOn(v => !v)} role="switch" aria-checked={afterburnerOn}><span className="knob"></span></button>
            </div>
          </div>
        </div>

        {/* Preflight summary */}
        <div className="preflight">
          <div className="sec-title" style={{ marginBottom: 14 }}>Pre-flight</div>
          {!autoDetect && c && (
            <div className="row gap-10" style={{ marginBottom: 14 }}>
              {standard ? <span className="std-ico pf"><I.doc size={17} /></span> : <ClientBadge slug={client} size={38} />}
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{c.name}</div>
                <div className="muted mono" style={{ fontSize: 11 }}>{standard ? stdLayout.desktop + 'px · universal rules · no references' : c.width + 'px · ' + c.font + ' · ' + c.refs + ' refs'}</div>
              </div>
            </div>
          )}
          {!autoDetect && c && <div className="row gap-6 wrap" style={{ marginBottom: 14 }}>{c.flags.map(f => <span key={f} className="chip" style={{ fontSize: 10.5 }}>{f}</span>)}</div>}
          <div className="pf-row"><span className="pf-k">Variant</span><span className="pf-v" data-selected-variant-profile>
            {!standard && selectedVariant ? (
              <span className="pf-vchip" title={selectedVariant.profile_id}
                style={{ '--vh': selectedHue.dot, '--vh-ink': selectedHue.ink, '--vh-tint': selectedHue.tint }}>
                <i aria-hidden="true"></i>{selectedVariant.scope.variant_id || selectedVariant.profile_id}
              </span>
            ) : standard ? 'No client variant' : 'Client default'}
          </span></div>
          {standard && <div className="pf-row"><span className="pf-k">Optimized</span><span className="pf-v">{stdLayout.clients.length ? stdLayout.clients.map(k => ({ apple: 'Apple Mail', gmail: 'Gmail', outlook: 'Outlook' })[k]).join(' · ') : 'No client tuning'}</span></div>}
          {standard && <div className="pf-row"><span className="pf-k">Layout</span><span className="pf-v">{stdLayout.mode.charAt(0).toUpperCase() + stdLayout.mode.slice(1)} · {stdLayout.desktop}{stdLayout.mode === 'fixed' ? 'px' : ' → ' + stdLayout.mobile + 'px'}</span></div>}
          <div className="pf-row"><span className="pf-k">Source</span><span className="pf-v">{source.toUpperCase()}</span></div>
          <div className="pf-row"><span className="pf-k">Engine</span><span className="pf-v">{agentObj.label}</span></div>
          {espDetail ? (
            espDetail.list.length ? (
              <React.Fragment>
                <div className="pf-row" data-esp-platforms={espDetail.list.length}><span className="pf-k">{espDetail.list.length > 1 ? 'Platforms' : 'Platform'}</span><span className="pf-v">{espDetail.list.length} selected</span></div>
                {espDetail.list.map(e => (
                  <div className="pf-row pf-sub" key={e.esp} data-esp-preflight={e.esp}>
                    <span className="pf-k">{e.esp}</span>
                    <span className="pf-v" style={{ color: e.esp_tier === 'advanced' ? 'var(--brand-orange)' : null }}>{e.esp_tier === 'advanced' ? 'Advanced' : 'Basic'}</span>
                  </div>
                ))}
                {espDetail.notes && espDetail.notes.trim() && <div className="pf-row"><span className="pf-k">ESP instructions</span><span className="pf-v">Included</span></div>}
              </React.Fragment>
            ) : <div className="pf-row"><span className="pf-k">Platform</span><span className="pf-v" style={{ color: 'var(--muted)' }}>No ESP · placeholders</span></div>
          ) : <div className="pf-row"><span className="pf-k">Platform</span><span className="pf-v">{autoDetect ? 'Auto-detect' : esp}</span></div>}
          {!SERVER_ACCEPTS.esps && <div className="pf-row pf-sub" data-esp-wire="held"><span className="pf-k">Wire</span><span className="pf-v">not sent yet · ships to SFMC (V1)</span></div>}
          {!autoDetect && presetObj && <div className="pf-row"><span className="pf-k">Preset</span><span className="pf-v" style={{ color: 'var(--brand-orange)' }}>{presetObj.name}</span></div>}
          {showModelTier && <div className="pf-row"><span className="pf-k">Tier</span><span className="pf-v">{tierObj.label}</span></div>}
          <div className="pf-row"><span className="pf-k">Est. cost</span><span className="pf-v" title={estCostTitle} data-est-cost-live={liveCostReady ? '1' : '0'}>{estCostLabel}</span></div>
          {showModelTier && <div className="pf-row"><span className="pf-k">Models</span><span className="pf-v">{tier === 'max' ? '4' : '5'} stages</span></div>}
          <div className="pf-row"><span className="pf-k">Custom notes</span><span className="pf-v">{instr.trim() ? 'Included' : '—'}</span></div>
          <div className="pf-row"><span className="pf-k">Extra files</span><span className="pf-v">{extraFiles.length || '—'}</span></div>
          <div className="pf-row"><span className="pf-k">Afterburner QA</span><span className="pf-v" style={afterburnerOn ? { color: 'var(--pass)' } : null}>{afterburnerOn ? 'On' : 'Off'}</span></div>
          <div className="pf-row"><span className="pf-k">Budget after</span><span className="pf-v" title={budgetAfterTitle} data-budget-live={budgetAvail ? '1' : '0'} style={{ color: budgetAvail ? 'var(--pass)' : 'var(--faint)' }}>{budgetAfterLabel}</span></div>
          <button
            className="btn btn-primary btn-lg mt-16"
            style={{ width: '100%', justifyContent: 'center' }}
            disabled={starting || (source === 'figma' && !figmaUrl.trim()) || (agent === 'sabre' && !sabreEligible) || (standard && !SERVER_ACCEPTS.standard)}
            onClick={async () => {
              setStartError(null);
              const testId = (buildName || '').trim();
              if (!/^[A-Za-z0-9_-]{1,64}$/.test(testId)) {
                setStartError('Build name must be 1–64 chars: letters, numbers, _ or -.'); return;
              }
              if (agent === 'sabre' && !sabreEligible) {
                if (standard) { setStartError('Sabre builds Synchrony PDFs only — choose Codex for a Standard HTML build'); return; }
                setStartError('Sabre currently requires Synchrony, PDF, Standard, and a real uploaded bundle.'); return;
              }
              if (source === 'figma' && !figmaUrl.trim()) { setStartError('Figma URL required.'); return; }
              const payload = startPayload({
                source, buildName, client, autoDetect, engine: agentObj.engine, tier,
                esp, espSummary, espDetail, preset, afterburnerOn, instr, variantProfileId, figmaUrl, uploadId,
                standard, layout: stdLayout,
              });
              if (payload.blocked) {
                setStartError("Standard HTML builds aren't accepted by the build server yet"); return;
              }
              setStarting(true);
              const startedContext = window.MAV.__contextKey;
              const startedEpoch = window.MAV.__contextEpoch;
              try {
                const r = await window.GooseAPI.startBuild(payload);
                if (window.MAV.__contextKey !== startedContext || window.MAV.__contextEpoch !== startedEpoch) return;
                refreshSavedStarts();
                if (r?.pending) {
                  setStartError(`Build ${r.job_id || 'request'} is awaiting confirmation (${r.dispatch_status || 'pending'}). Retry with the same inputs to check the original request; do not create another build.`);
                  return;
                }
                if (r?.dispatch_status === 'publication_failed') {
                  setStartError(`Build ${r.job_id} could not be dispatched. Its evidence has been retained; use job recovery rather than submitting another paid build.`);
                  return;
                }
                // Surface the job_id for the smoke + dev console; not user-facing copy
                if (r && r.job_id) window.__lastStartedJob = r.job_id;
                // Stub the new build into MAV.jobs IMMEDIATELY so the dashboard
                // table, sidebar count, and JobDetail can render the moment we
                // navigate. The projector takes up to 15s to write the real
                // row; the delayed re-hydrate below replaces the stub with the
                // real DB-backed row once it lands.
                if (r && r.job_id) {
                  const stages = (window.MAV.stageDefs || []).map((d, i) => ({
                    ...d,
                    name: agent === 'sabre' ? SABRE_STAGE_NAMES[i] : d.name,
                    model: agent === 'sabre' ? (i === 2 ? 'Sabre reasoner' : 'deterministic') : d.model,
                    status: 'queued', tokens: '—', elapsed: '—',
                  }));
                  const stub = {
                    id: r.job_id,
                    name: testId,
                    client: standard ? 'generic' : (agent === 'sabre' ? client : (autoDetect ? 'generic' : client)),
                    source: r.source || source,
                    engine: agentObj.engine,   // PY-10: drive the engine-aware artifact tree + label (Goose-16)
                    status: r.status || 'queued', // published is not proof a worker started
                    verdict: null,
                    owner: 'You',
                    started: 'just now',
                    tokensK: 0, cost: 0,
                    confidence: null, activeStage: 0,
                    stages,
                    _id: null,   // no DB uuid yet — the detail fetch in JobDetail
                                 // skips when _id is missing; the delayed hydrate
                                 // below replaces this stub with the real row.
                    _live: true, _stub: true,
                    _contextKey: startedContext,
                  };
                  // Prepend so the dashboard's "Recent builds" surfaces it first
                  window.MAV.jobs = [stub, ...window.MAV.jobs];
                }
                // Goose-chase intro animation if the prototype's hook is ever
                // wired up (was prototype-only). The async navigation below
                // doesn't wait on it.
                if (typeof window.gnPlayIntro === 'function') {
                  try { window.gnPlayIntro(); } catch (_) {}
                }
                // Navigate to the new build's detail page immediately so the user
                // sees their build's status, not the dashboard.
                if (r && r.job_id && typeof openJob === 'function') {
                  openJob(r.job_id);
                } else {
                  go('home');
                }
                // Delayed re-hydrate: the projector ticks every 15s; one tick
                // after spawn should have the build row in the DB. We wait 18s
                // (one tick + a small margin) and refresh so the stub gets
                // replaced with the real DB row + uuid.
                setTimeout(() => {
                  if (window.GooseHydrate && window.MAV.__contextKey === startedContext && window.MAV.__contextEpoch === startedEpoch) {
                    Promise.resolve(window.GooseHydrate()).catch(() => {});
                  }
                }, 18000);
              } catch (e) {
                if (window.MAV.__contextKey !== startedContext || window.MAV.__contextEpoch !== startedEpoch) return;
                refreshSavedStarts();
                setStartError((e && (e.data && e.data.error)) || e.message || 'Failed to start build.');
              } finally {
                if (window.MAV.__contextKey === startedContext && window.MAV.__contextEpoch === startedEpoch) setStarting(false);
              }
            }}
            data-action="run-build"
            data-engine={agentObj.engine}
          >
            {starting ? (<React.Fragment><span className="spinner" /> Starting…</React.Fragment>) : (<React.Fragment><I.bolt size={16} /> Run build</React.Fragment>)}
          </button>
          {startError && <p style={{ fontSize: 12, color: 'var(--fail)', marginTop: 10, textAlign: 'center' }} data-build-error>{startError}</p>}
          {standard && !SERVER_ACCEPTS.standard && <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>Standard HTML builds aren't accepted by the build server yet</p>}
          {standard && agent === 'sabre' && <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>Sabre builds Synchrony PDFs only — choose Codex for a Standard HTML build</p>}
          <p className="muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 10 }}>Queues immediately · runs server-side</p>
        </div>
      </div>
      {statsClient && <ClientBuildsModal slug={statsClient} onClose={() => setStatsClient(null)} />}
    </div>
  );
}
window.NewBuild = NewBuild;
